import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const INITIAL_MSG = {
  role: "assistant",
  text: "Hi! 👋 I'm your MR Bus AI assistant.\n\nI can help with:\n🔍 Search buses\n📋 My bookings\n❌ Cancel / 🔄 Reschedule\n🏆 Loyalty points\n📧 Resend receipt\n\nJust ask me anything!",
};

export default function GlobalChatWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing]);

  useEffect(() => {
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setTyping(true);
    try {
      const token = localStorage.getItem("token");
      const history = messages.slice(-8).map(m => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
      const res = await fetch(`${process.env.REACT_APP_API_URL}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, token: token || null, history }),
      });
      const data = await res.json();
      setTyping(false);
      const reply = data.response || "Sorry, something went wrong!";
      setMessages(prev => [...prev, { role: "assistant", text: reply }]);
      if (!open) setUnread(n => n + 1);

      // Handle navigation actions
      if (data.action === "show_bookings" || data.action === "refresh_bookings") {
        setTimeout(() => navigate("/my-bookings"), 1200);
      }
      if (data.action === "show_reviews") setTimeout(() => navigate("/reviews"), 800);
      if (data.action === "show_profile") setTimeout(() => navigate("/profile"), 800);
      if (data.action === "show_subscription") setTimeout(() => navigate("/subscription"), 800);
    } catch {
      setTyping(false);
      setMessages(prev => [...prev, { role: "assistant", text: "⚠️ AI unavailable. Please try again." }]);
    }
  };

  const quickReplies = ["Show my bookings", "My loyalty points", "Help"];

  return (
    <>
      {/* Chat window */}
      {open && (
        <div style={{
          position: "fixed", bottom: "80px", right: "20px",
          width: "min(340px, calc(100vw - 24px))", maxHeight: "min(520px, 75vh)",
          background: "#fff", borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.22), 0 4px 16px rgba(0,0,0,0.12)",
          display: "flex", flexDirection: "column",
          fontFamily: "'Outfit', sans-serif",
          zIndex: 9999, overflow: "hidden",
          border: "1px solid rgba(249,115,22,0.15)",
        }}>
          {/* Header */}
          <div style={{
            background: "linear-gradient(135deg,#0e0618,#1a0a2e)",
            padding: "14px 18px", display: "flex", alignItems: "center",
            justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                width: "32px", height: "32px", borderRadius: "50%",
                background: "linear-gradient(135deg,#f97316,#ea580c)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "16px",
              }}>🚌</div>
              <div>
                <div style={{ color: "#fff", fontWeight: "700", fontSize: "13px" }}>MR Bus AI</div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#4ade80" }} />
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px" }}>Online · Replies instantly</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: "28px", height: "28px", borderRadius: "50%", cursor: "pointer", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              ×
            </button>
          </div>

          {/* Messages */}
          <div ref={bodyRef} style={{
            flex: 1, overflowY: "auto", padding: "14px",
            display: "flex", flexDirection: "column", gap: "10px",
            background: "#faf8f5",
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start",
              }}>
                <div style={{
                  maxWidth: "82%",
                  background: m.role === "user"
                    ? "linear-gradient(135deg,#f97316,#ea580c)"
                    : "#fff",
                  color: m.role === "user" ? "#fff" : "#1a1207",
                  padding: "10px 13px",
                  borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  fontSize: "12.5px", lineHeight: "1.5",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  whiteSpace: "pre-wrap",
                  border: m.role === "user" ? "none" : "1px solid #f0ebe4",
                }}>
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{
                  background: "#fff", border: "1px solid #f0ebe4",
                  padding: "10px 14px", borderRadius: "14px 14px 14px 4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  display: "flex", gap: "4px", alignItems: "center",
                }}>
                  {[0, 1, 2].map(d => (
                    <div key={d} style={{
                      width: "6px", height: "6px", borderRadius: "50%",
                      background: "#f97316", opacity: 0.7,
                      animation: `bounce 1.2s ease-in-out ${d * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick replies */}
          {messages.length <= 2 && (
            <div style={{ padding: "8px 14px 4px", display: "flex", gap: "6px", flexWrap: "wrap", background: "#faf8f5" }}>
              {quickReplies.map(q => (
                <button key={q} onClick={() => send(q)}
                  style={{
                    background: "#fff7ed", border: "1px solid #fed7aa",
                    color: "#c2410c", padding: "5px 10px", borderRadius: "20px",
                    fontSize: "11px", fontWeight: "600", cursor: "pointer",
                    fontFamily: "'Outfit', sans-serif",
                  }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: "10px 12px", borderTop: "1px solid #f0ebe4",
            display: "flex", gap: "8px", alignItems: "center", background: "#fff",
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Ask anything..."
              style={{
                flex: 1, border: "1.5px solid #e8e2d9", borderRadius: "12px",
                padding: "9px 13px", fontSize: "12.5px", outline: "none",
                fontFamily: "'Outfit', sans-serif", color: "#1a1207",
                background: "#faf8f5",
              }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim()}
              style={{
                width: "36px", height: "36px", borderRadius: "12px",
                background: input.trim() ? "linear-gradient(135deg,#f97316,#ea580c)" : "#f0ebe4",
                border: "none", cursor: input.trim() ? "pointer" : "default",
                color: input.trim() ? "#fff" : "#b0a090",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "16px", flexShrink: 0,
              }}>
              ➤
            </button>
          </div>
          <div style={{ padding: "4px", textAlign: "center", background: "#fff" }}>
            <span style={{ fontSize: "9px", color: "#c8bfb5" }}>Powered by MR Bus AI</span>
          </div>
        </div>
      )}

      {/* FAB button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: "fixed", bottom: "20px", right: "20px",
          width: "56px", height: "56px", borderRadius: "50%",
          background: open ? "#1a0a2e" : "linear-gradient(135deg,#f97316,#ea580c)",
          border: "none", cursor: "pointer",
          boxShadow: "0 8px 24px rgba(249,115,22,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "24px", zIndex: 10000,
          transition: "all 0.2s",
        }}>
        {open ? "×" : "🚌"}
        {!open && unread > 0 && (
          <div style={{
            position: "absolute", top: "-2px", right: "-2px",
            background: "#ef4444", color: "#fff",
            width: "18px", height: "18px", borderRadius: "50%",
            fontSize: "10px", fontWeight: "800",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid #fff",
          }}>{unread}</div>
        )}
      </button>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}
